import { useState, useEffect, useCallback, useRef } from 'react';

export interface UseDataFetchOptions<T> {
  /** Initial data value */
  initialData?: T | null;
  /** Enable/disable automatic fetch on mount */
  enabled?: boolean;
  /** Dependencies that trigger refetch when changed */
  dependencies?: unknown[];
  /** Transform the response data */
  transform?: (data: T) => T;
  /** Callback on successful fetch */
  onSuccess?: (data: T) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
  /** Retry count on failure */
  retryCount?: number;
  /** Delay between retries (ms) */
  retryDelay?: number;
  /** Cache key for deduplication */
  cacheKey?: string;
  /** Stale time (ms) - how long to consider cached data fresh */
  staleTime?: number;
}

export interface UseDataFetchResult<T> {
  data: T | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isFetching: boolean;
  isSuccess: boolean;
  refetch: () => Promise<void>;
  reset: () => void;
}

// Simple in-memory cache
const cache = new Map<string, { data: unknown; timestamp: number }>();

export function useDataFetch<T>(
  fetchFn: () => Promise<T>,
  options: UseDataFetchOptions<T> = {}
): UseDataFetchResult<T> {
  const {
    initialData = null,
    enabled = true,
    dependencies = [],
    transform,
    onSuccess,
    onError,
    retryCount = 0,
    retryDelay = 1000,
    cacheKey,
    staleTime = 0,
  } = options;

  const [data, setData] = useState<T | null>(initialData);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [isFetching, setIsFetching] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const retryCountRef = useRef(0);
  const isMountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    // Check cache first
    if (cacheKey) {
      const cached = cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < staleTime) {
        setData(cached.data as T);
        setIsLoading(false);
        setIsSuccess(true);
        return;
      }
    }

    setIsFetching(true);
    setError(null);

    try {
      let result = await fetchFn();

      // Apply transform if provided
      if (transform) {
        result = transform(result);
      }

      if (!isMountedRef.current) return;

      setData(result);
      setIsSuccess(true);
      setError(null);
      retryCountRef.current = 0;

      // Update cache
      if (cacheKey) {
        cache.set(cacheKey, { data: result, timestamp: Date.now() });
      }

      onSuccess?.(result);
    } catch (err) {
      if (!isMountedRef.current) return;

      const error = err instanceof Error ? err : new Error(String(err));

      // Retry logic
      if (retryCountRef.current < retryCount) {
        retryCountRef.current += 1;
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        if (isMountedRef.current) {
          return fetchData();
        }
      }

      setError(error);
      setIsSuccess(false);
      onError?.(error);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
        setIsFetching(false);
      }
    }
  }, [fetchFn, transform, onSuccess, onError, retryCount, retryDelay, cacheKey, staleTime]);

  const refetch = useCallback(async () => {
    retryCountRef.current = 0;
    // Clear cache on manual refetch
    if (cacheKey) {
      cache.delete(cacheKey);
    }
    await fetchData();
  }, [fetchData, cacheKey]);

  const reset = useCallback(() => {
    setData(initialData);
    setError(null);
    setIsLoading(false);
    setIsFetching(false);
    setIsSuccess(false);
    retryCountRef.current = 0;
    if (cacheKey) {
      cache.delete(cacheKey);
    }
  }, [initialData, cacheKey]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (enabled) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...dependencies]);

  return {
    data,
    isLoading,
    isError: !!error,
    error,
    isFetching,
    isSuccess,
    refetch,
    reset,
  };
}

// Clear all cached data
export const clearDataFetchCache = () => {
  cache.clear();
};

// Clear specific cache entry
export const invalidateCache = (key: string) => {
  cache.delete(key);
};

export default useDataFetch;
